<template>
  <div>
    <v-autocomplete
      ref="autoCompleteInput"
      :data-intro="$t('message.introJS.videos.search')"
      :placeholder="placeholder"
      :items="filteredVideoList"
      :loading="loadingSearchBar"
      :append-icon="''"
      prepend-inner-icon="mdi-magnify"
      aria-autocomplete="off"
      autocomplete="off"
      class="autocomplete"
      hide-selected
      hide-details
      no-filter
      dense
      solo
      flat
      @update:search-input="filterSearch"
      @keydown="checkIfEscWasPressed"
      @change="handleClickAutocompleteRow"
      @blur="handleSearchBlur"
    >
      <template v-slot:no-data>
        <v-list-item>
          <v-list-item-title>
            <span v-if="searchedText && searchedText.length > 0"
              >{{ $t('message.pages.videosDataTable.searchBar.noData') }}
              {{
                $t('message.pages.videosDataTable.searchBar.searched') +
                ' ' +
                searchedText
              }}</span
            >
            <span v-else>{{
              $t('message.pages.videosDataTable.searchBar.typeToSearch')
            }}</span>
          </v-list-item-title>
        </v-list-item>
      </template>
      <template v-slot:selection="{ attr, on, item, selected }">
        <span v-bind="attr" v-on="on" :input-value="selected">
          <span>{{ item.name ? item.name : item.title }}</span>
        </span>
      </template>
      <template
        v-slot:prepend-item
        v-if="
          searchedText &&
          ($route.name === 'Videos' || $route.name === 'Folders')
        "
      >
        <div
          @mousedown="isClickingFilterContainer = true"
          @mouseup="isClickingFilterContainer = false"
          class="filter-click-container"
        >
          <div class="d-flex filter-container">
            <v-btn
              v-for="(filterItem, index) in filters"
              :key="`${filterItem.value}--${index}`"
              class="px-0"
              x-small
              block
              @click="handleChangeFilter(filterItem.value)"
              :style="getBtnStyle(index, filterItem.value === filter)"
              color="primary"
              elevation="0"
              tile
              :outlined="filterItem.value !== filter"
              :plain="filterItem.value !== filter"
            >
              {{ filterItem.text }}
            </v-btn>
          </div>
        </div>
      </template>
      <template v-slot:item="{ item }">
        <!-- Live search -->
        <v-row
          no-gutters
          @click="handleClickAutocompleteRow(item)"
          v-if="item.hasOwnProperty('rtmp')"
        >
          <v-list-item-avatar class="icon-container">
            <v-icon
              :color="
                !$vuetify.theme.dark ? 'var(--v-neutral-lighten1)' : '#FFF'
              "
              size="22px"
            >
              mdi-access-point
            </v-icon>
          </v-list-item-avatar>
          <v-list-item-content class="py-0">
            <v-list-item-title>
              <v-row no-gutters class="d-flex flex-column">
                <span
                  class="item-title text-truncate"
                  :title="item.name || item.title"
                  >{{ item.name || item.title }}</span
                >
              </v-row>
            </v-list-item-title>
          </v-list-item-content>
        </v-row>
        <!-- A/B Test search -->
        <v-row
          no-gutters
          @click="handleClickAutocompleteRow(item)"
          v-else-if="item.hasOwnProperty('winner_video_id')"
          class="row-search-item"
        >
          <v-list-item-avatar class="icon-container">
            <octicon
              name="video"
              :color="
                !$vuetify.theme.dark ? 'var(--v-neutral-lighten1)' : '#FFF'
              "
              size="22"
            />
          </v-list-item-avatar>
          <v-list-item-content class="py-0">
            <v-list-item-title>
              <v-row no-gutters class="d-flex flex-column">
                <span class="item-title text-truncate" :title="item.name">{{
                  item.name
                }}</span>
              </v-row>
            </v-list-item-title>
          </v-list-item-content>
        </v-row>
        <!-- Video search -->
        <v-row
          no-gutters
          @click="handleClickAutocompleteRow(item)"
          class="row-search-item"
          @contextmenu.prevent="(e) => show(item, e)"
          v-else
        >
          <v-list-item-avatar class="icon-container">
            <v-icon v-if="item.name"> mdi-folder </v-icon>
            <octicon
              name="video"
              :color="
                !$vuetify.theme.dark ? 'var(--v-neutral-lighten1)' : '#FFF'
              "
              size="22"
              v-else
            />
          </v-list-item-avatar>
          <v-list-item-content class="py-0">
            <v-list-item-title>
              <v-row no-gutters class="d-flex flex-column">
                <span
                  :class="[item.status === 'DELETING' && 'deletedText']"
                  class="item-title text-truncate"
                  :title="item.name || item.title"
                  >{{ item.name || item.title }}</span
                >
                <template v-if="getFolderPathById(item)">
                  <v-hover v-slot="{ hover }">
                    <v-col
                      v-if="item.status != 'DELETING'"
                      class="d-flex align-center"
                    >
                      <div class="folder-chain"></div>
                      <span
                        class="path-text"
                        :class="[
                          hover && 'text-underlined',
                          'text-caption text-truncate',
                        ]"
                        @click="handleClickUnderlinedFolder(item)"
                        :title="getFolderPathById(item)"
                      >
                        {{ getFolderPathById(item) }}
                      </span>
                    </v-col>
                  </v-hover>
                </template>
              </v-row>
            </v-list-item-title>
          </v-list-item-content>
        </v-row>
      </template>
    </v-autocomplete>
    <!-- context menu -->
    <VideoContextMenu
      :menuItem="menuItem"
      :selectedItems="[]"
      :downloadServer="null"
      :drmGroupsLoading="drmGroupsLoading"
      :isDrmActive="isDrmActive"
      :drmGroups="drmGroups"
      :xMenu="xMenu"
      :yMenu="yMenu"
      :showMenu="showMenu"
      @setShowMenu="(data) => (showMenu = data)"
    />
  </div>
</template>

<script>
import { mapState, mapMutations } from 'vuex'
import Fuse from 'fuse.js'
import videosListMixin from '@/utils/videosList.mixin.js'
import octicons from '@primer/octicons'
import Vue from 'vue'
import i18n from '@/translations/lang.js'
import VideoContextMenu from '@/components/VideoContextMenu.vue'

Vue.component('octicon', {
  props: {
    name: String,
    size: String,
    color: String,
  },
  computed: {
    svg() {
      return octicons[this.name].toSVG({
        height: this.size,
        width: this.size,
        fill: this.color,
      })
    },
  },
  template: '<div v-html="svg" class="octicon-container"></div>',
})

export default {
  name: 'SearchBar',
  components: {
    VideoContextMenu,
  },
  props: {
    fuseOptions: {
      required: true,
      type: Object,
    },
    list: {
      required: true,
    },
    placeholder: {
      type: String,
      default: '',
      required: false,
    },
  },
  watch: {
    list() {
      this.filterSearch(this.searchedText)
    },
  },
  data: () => ({
    isClickingFilterContainer: false,
    filter: 'all',
    filters: [
      {
        value: 'all',
        text: i18n.t('message.pages.videosDataTable.searchBar.filters.all'),
      },
      {
        value: 'videos',
        text: i18n.t('message.pages.videosDataTable.searchBar.filters.videos'),
      },
      {
        value: 'folders',
        text: i18n.t('message.pages.videosDataTable.searchBar.filters.folders'),
      },
    ],
    searchedText: '',
    loadingSearchBar: false,
    filteredVideoList: [],
    underlinedClick: false,
    showMenu: false,
    menuItem: undefined,
    xMenu: 0,
    yMenu: 0,
    blockBlur: false,
    module: null,
  }),
  mixins: [videosListMixin, module],
  async created() {
    document.onclick = null
    this.$root.$off('VideoDeleteModalHasClosed', this.handleReRenderSearchBar)
    this.$root.$off(
      'VideoDuplicateModalHasClosed',
      this.handleReRenderSearchBar
    )
    this.$root.$off('MoveModalHasClosed', this.handleReRenderSearchBar)
    this.$root.$off('VideoEmbedModalHasClosed', this.handleReRenderSearchBar)
    this.$root.$off('VideoModalRenameHasClosed', this.handleReRenderSearchBar)
    this.$root.$off('FolderModalRenameHasClosed', this.handleReRenderSearchBar)
    this.$root.$off('DrmModalHasClosed', this.handleReRenderSearchBar)

    if (!this.module) {
      if (window.location.hash === '#/lives') {
        this.module = await import('@/utils/livesListV2.mixin')
      } else {
        this.module = await import('@/utils/livesList.mixin')
      }
    }
  },
  mounted() {
    document.onkeydown = function (e) {
      if (e.key === 'f' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault()
      }
    }
    document.onclick = () => {
      if (
        document.querySelector('.v-overlay--active') ||
        document.querySelector('.v-dialog__content--active')
      ) {
        this.blockBlur = true
      } else {
        this.blockBlur = false
        const menuElement = document.querySelector(
          '.v-menu__content.menuable__content__active.v-autocomplete__content'
        )
        setTimeout(() => {
          if (
            this.$refs.autoCompleteInput &&
            this.$refs.autoCompleteInput.$el.style?.width === '30vw' &&
            !this.$refs.autoCompleteInput.isFocused
          ) {
            if (menuElement) menuElement.style.display = 'none'
            this.handleSearchBlur()
          }
        }, 0)
      }
    }
    this.$root.$on('VideoDeleteModalHasClosed', this.handleReRenderSearchBar)
    this.$root.$on('VideoDuplicateModalHasClosed', this.handleReRenderSearchBar)
    this.$root.$on('MoveModalHasClosed', this.handleReRenderSearchBar)
    this.$root.$on('VideoEmbedModalHasClosed', this.handleReRenderSearchBar)
    this.$root.$on('VideoModalRenameHasClosed', this.handleReRenderSearchBar)
    this.$root.$on('FolderModalRenameHasClosed', this.handleReRenderSearchBar)
    this.$root.$on('DrmModalHasClosed', this.handleReRenderSearchBar)
  },
  computed: {
    ...mapState('user', {
      mainFolders: 'mainFolders',
      isDrmActive: (state) => state.drmData.isDrmActive,
      drmGroupsLoading: (state) => state.drmData.loadings.drmGroups,
      drmGroups: (state) => state.drmData.drmGroups,
    }),
  },
  methods: {
    ...mapMutations('user', ['UPDATE_ROUTED_VIDEO', 'UPDATE_GLOBAL_SNACKBAR']),
    handleReRenderSearchBar() {
      const menuElement = document.querySelector(
        '.v-menu__content:has(.filter-click-container)'
      )
      if (menuElement) {
        if (
          !this.$refs.autoCompleteInput?.isFocused &&
          menuElement.style.display === 'block'
        ) {
          menuElement.style.display = 'none'
          this.handleSearchBlur()
        }
      }
    },
    handleChangeFilter(val) {
      this.filter = val
      this.$emit('filterList', val)
    },
    show(item, e) {
      this.menuItem = item
      this.showMenu = false
      this.xMenu = e.clientX
      this.yMenu = e.clientY
      this.$nextTick(() => {
        this.showMenu = true
      })
    },
    getBtnStyle(index, isSelected) {
      if (index === 0) {
        return `border-radius: 4px 0px 0px 4px; ${
          isSelected
            ? 'border-right: 1px solid transparent'
            : 'border-right: none'
        }`
      } else if (index === 2) {
        return `border-radius: 0px 4px 4px 0px; ${
          isSelected
            ? 'border-left: 1px solid transparent'
            : 'border-left: none'
        }`
      }
    },
    getFolderPathById(item) {
      const rootString = ''
      if (!item) return rootString
      if (item.name && !item.parent_folder_id) return rootString
      if (item.title && item.folder_id === 'root') return rootString
      let folders = []
      let set = true
      let toBeSearchedItem = item
      let nextFolder = {}
      while (set) {
        nextFolder = this.findFolderById(
          toBeSearchedItem.name
            ? toBeSearchedItem.parent_folder_id
            : toBeSearchedItem.folder_id
        )
        if (toBeSearchedItem.id === nextFolder?.parent_folder_id) {
          break
        }
        if (nextFolder?.name) {
          folders.push(nextFolder)
          toBeSearchedItem = nextFolder
        } else {
          break
        }
      }
      let pathString = ''
      let addedString = ''
      for (let folder of folders.reverse()) {
        addedString = `${folder.name}/`
        pathString += addedString
      }
      return pathString
    },
    handleClickUnderlinedFolder(item) {
      this.underlinedClick = true
      this.$refs.autoCompleteInput.clearableCallback()
      this.$refs.autoCompleteInput.isFocused = false
      if (item.name) {
        if (item.parent_folder_id) {
          let parentFolder = this.findFolderById(item.parent_folder_id)
          this.handleDoubleClickRow(parentFolder)
        } else {
          this.handleRoot()
        }
      }
      if (item.title) {
        let parentFolder = this.findFolderById(item.folder_id)
        !parentFolder
          ? this.handleRoot()
          : this.handleDoubleClickRow(parentFolder)
      }
      setTimeout(() => {
        this.underlinedClick = false
      }, 300)
    },
    handleClickAutocompleteRow(item) {
      if (this.underlinedClick) return
      if (!item) return
      if (item.hasOwnProperty('winner_video_id')) {
        return this.$router.push({ name: 'NewTest', params: { id: item.id } })
      }
      if (item.hasOwnProperty('rtmp')) {
        return this.goToPainel(item)
      }
      if (item.status === 'DELETING') {
        this.$router.push({
          name: 'Trash',
          query: { search: item.title || item.name },
        })
        return
      }
      if (item.status === 'DRAFT' && !item.created_at) {
        this.UPDATE_GLOBAL_SNACKBAR({
          open: true,
          text: i18n.t(
            'message.pages.videosDataTable.searchBar.waitConversion'
          ),
        })
      }
      this.$refs.autoCompleteInput.clearableCallback()
      this.$refs.autoCompleteInput.isFocused = false
      this.handleDoubleClickRow(item)
    },

    checkIfEscWasPressed(event) {
      if (event.keyCode === 27) {
        this.searchedText = ''
      }
    },
    handleSearchBlur() {
      if (this.blockBlur || this.showMenu) return
      if (this.isClickingFilterContainer) {
        this.$refs.autoCompleteInput.focus()
        return
      }
      this.searchedText = ''
      this.filteredVideoList = []
      this.$root.$emit('resetIsAnyOtherModal', false)
    },
    checkIfStringHasId(val) {
      let searchValue = val
      if (searchValue) {
        let hasPandaHls = val
          .split('tv.pandavideo.com')
          .pop()
          .split('/playlist.m3u8')[0]
          .split('/')
          .pop()
        if (hasPandaHls && hasPandaHls.length === 36) {
          searchValue = hasPandaHls
          return searchValue
        }
        let hasEmbedValue = val
          .split('tv.pandavideo.com')
          .pop()
          .split('/embed/?v=')
          .pop()
          .substr(0, 36)
        if (hasEmbedValue && hasEmbedValue.length === 36) {
          searchValue = hasEmbedValue
          return searchValue
        }
        let hasVimeoId = searchValue.includes('vimeo')
        let isLink = searchValue.includes('https')
        if (hasVimeoId && isLink) {
          searchValue = `vimeo-${searchValue.split('vimeo.com/').pop()}`
          return searchValue
        }
      }
      return false
    },
    async filterSearch(typedValue) {
      this.loadingSearchBar = true
      this.searchedText = typedValue
      typedValue = this.checkIfStringHasId(typedValue)
        ? this.checkIfStringHasId(typedValue)
        : typedValue
      if (typedValue && typedValue.length > 0) {
        const list = await this.list
        const fuse = new Fuse(list, this.fuseOptions)
        let searchResult = fuse.search(typedValue)

        const itemsResults = searchResult.map((character) => character.item)
        this.filteredVideoList = itemsResults
      } else {
        this.filteredVideoList = []
      }
      this.loadingSearchBar = false
    },
    async goToPainel({ vod_id, id }) {
      if (!this.module) await this.$nextTick()

      return this.module?.default?.methods?.goToPainel.bind(this)({
        vod_id,
        id,
      })
    },
  },
}
</script>
<style lang="scss">
.v-menu__content:has(.filter-click-container) {
  z-index: 8 !important;
}
</style>
<style lang="scss" scoped>
@import '@/assets/scss/src/autocomplete';

.path-text {
  color: var(--v-neutral-lighten1) !important;
  display: block;
  max-width: calc(90% - 10px);
}

.item-title {
  line-height: 24px;
  color: var(--v-brand-base) !important;
  font-size: 16px;
  margin-bottom: 1px;
  max-width: calc(100% - 20px);
}

.icon-container {
  height: unset;
  min-width: unset;
  width: unset;
  background: transparent;
  margin-left: 8px;
  margin-right: 18px;
}

.octicon-container {
  margin: 0 auto;
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
}

.row-search-item {
  max-width: 100%;
}

.filter-container {
  width: 154px;
  margin: 16px 23px 14px 24px;
}

.filter-click-container {
  display: flex;
  min-width: 100%;
  width: 100%;
}

::v-deep
  .filter-container
  .v-btn.v-btn--plain:not(.v-btn--active):not(.v-btn--loading):not(:focus):not(:hover)
  .v-btn__content {
  opacity: 1 !important;
}
</style>
